import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Copy, RotateCw, Ban, Download, Trash2, ShieldCheck, FileText } from "lucide-react";

export default function ClientDocuments({ clientId }) {
  const [portal, setPortal] = useState(null);
  const [files, setFiles] = useState([]);
  const [docsCsv, setDocsCsv] = useState("ID, Proof of address");

  const load = async () => {
    const [p, f] = await Promise.all([
      api.get(`/clients/${clientId}/portal`),
      api.get(`/portal-files`, { params: { client_id: clientId } }),
    ]);
    setPortal(p.data?.token ? p.data : null);
    setFiles(f.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const create = async (rotate = false) => {
    const docs = docsCsv.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      const r = await api.post(`/clients/${clientId}/portal`, { docs_requested: docs, rotate });
      setPortal(r.data);
      toast.success(rotate ? "New link generated (old revoked)" : "Portal link created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this portal link? The client will no longer be able to upload.")) return;
    await api.post(`/clients/${clientId}/portal/revoke`);
    setPortal(null);
    toast.success("Portal revoked");
  };

  const portalUrl = portal ? `${window.location.origin}/portal/${portal.token}` : null;

  const copyUrl = () => {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl);
    toast.success("Link copied to clipboard");
  };

  const download = (fileId, name) => {
    // Auth-cookied download — open in new tab
    const url = `${API}/portal-files/${fileId}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.download = name || "file";
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  };

  const del = async (fileId) => {
    if (!window.confirm("Delete this uploaded document? This cannot be undone.")) return;
    await api.delete(`/portal-files/${fileId}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-md bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-slate-700" />
          <h3 className="font-heading text-base font-semibold text-slate-900">Secure client portal</h3>
        </div>
        {portal ? (
          <div className="space-y-3">
            <div>
              <Label>Portal URL (share with client)</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={portalUrl} data-testid="portal-url-input" className="font-mono text-xs" />
                <Button data-testid="copy-portal-btn" variant="outline" onClick={copyUrl}><Copy className="h-4 w-4" /></Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Expires <span className="font-mono">{fmtDate(portal.expires_at)}</span> · Docs requested: {portal.docs_requested?.join(", ")}
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="rotate-portal-btn" variant="outline" size="sm" onClick={() => create(true)}>
                <RotateCw className="h-3.5 w-3.5 mr-1" /> Rotate token
              </Button>
              <Button data-testid="revoke-portal-btn" variant="outline" size="sm" onClick={revoke} className="text-rose-600 hover:bg-rose-50">
                <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-slate-600">No active portal for this client. Generate a secure upload link:</div>
            <div>
              <Label>Documents to request (comma-separated)</Label>
              <Input data-testid="portal-docs-input" value={docsCsv} onChange={(e) => setDocsCsv(e.target.value)} />
            </div>
            <Button data-testid="create-portal-btn" onClick={() => create(false)} className="bg-slate-900 hover:bg-slate-800">Generate secure link</Button>
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">
            Received documents · {files.length} file(s)
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">File</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Size</th>
              <th className="text-left px-4 py-2">Uploaded</th>
              <th className="text-left px-4 py-2">From (IP)</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {files.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500"><FileText className="h-5 w-5 mx-auto text-slate-300 mb-2" />No documents yet.</td></tr>}
            {files.map((f) => (
              <tr key={f.file_id} data-testid="portal-file-row" className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900">{f.original_filename}</td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{f.content_type}</td>
                <td className="px-4 py-3 font-mono text-xs">{(f.size / 1024).toFixed(1)} KB</td>
                <td className="px-4 py-3 font-mono text-xs">{fmtDateTime(f.uploaded_at)}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{f.uploader_ip || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid="portal-download-btn" onClick={() => download(f.file_id, f.original_filename)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Download"><Download className="h-4 w-4" /></button>
                    <button data-testid="portal-delete-btn" onClick={() => del(f.file_id)} className="p-1.5 text-slate-400 hover:text-rose-600 rounded transition-colors" title="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
